import { State } from '../StateTypes';
import {
  ConfDialog,
  HasHadUserInteraction,
  HasShownExtendableConf,
  HasShownMountingRailSheet,
  HasShownPegboardHint,
  HasShownCuttableMountingRailHint,
  IntroPopupsVisible,
  Popups,
  ShowDoorsHint,
  ShowExtendableConf,
  ShowPegboardHint,
  ShowCuttableMountingRailHint,
  CuttableMountingRailHintAlignment,
  CuttableMountingRailHintCheckPending,
  ErrorHandlingPendingTimestamp,
} from './popupsTypes';
import { selectIsMeasurementsActive } from '../scene/sceneSelectors';

/**
 * Select popups state slice
 *
 * @param popups
 */
export const selectPopups = ({ popups }: State): Popups => popups;

/**
 * Select has had user interaction
 *
 * @param state
 * @returns {Validator<NonNullable<boolean>> | boolean}
 */
export const selectHasHadUserInteraction = (
  state: State
): HasHadUserInteraction => selectPopups(state).hasHadUserInteraction;

/**
 * Select has shown mounting rail sheet
 *
 * @param state
 * @returns {boolean}
 */
export const selectHasShownMountingRailSheet = (
  state: State
): HasShownMountingRailSheet => selectPopups(state).hasShownMountingRailSheet;

/**
 * Select conf dialog
 *
 * @param state
 * @returns {Requireable<Object> | {} | {open: boolean} | {closedId: *, open: boolean} | {closedId: null}}
 */
export const selectConfDialog = (state: State): ConfDialog =>
  selectPopups(state).confDialog;

/**
 * Select has shown extendable conf
 *
 * @param state
 * @returns {boolean}
 */
export const selectHasShownExtendableConf = (
  state: State
): HasShownExtendableConf => selectPopups(state).hasShownExtendableConf;

/**
 * Select show extendable conf
 *
 * @param state
 * @returns {null|*}
 */
export const selectShowExtendableConf = (state: State): ShowExtendableConf =>
  selectPopups(state).showExtendableConf;

/**
 * Select has shown pegboard hint
 *
 * @param state
 * @returns {boolean}
 */
export const selectHasShownPegboardHint = (
  state: State
): HasShownPegboardHint => selectPopups(state).hasShownPegboardHint;

/**
 * Select pegboard hint
 *
 * @param state
 * @returns {null | TacItem}
 */
export const selectPegboardHint = (state: State): ShowPegboardHint =>
  selectPopups(state).showPegboardHint;

/**
 * Select has shown cuttable mounting rail hint
 *
 * @param state
 * @returns {boolean}
 */
export const selectHasShownCuttableMountingRailHint = (
  state: State
): HasShownCuttableMountingRailHint =>
  selectPopups(state).hasShownCuttableMountingRailHint;

/**
 * Select cuttable mounting rail hint
 *
 * @param state
 * @returns {null | TacItem}
 */
export const selectCuttableMountingRailHint = (
  state: State
): ShowCuttableMountingRailHint =>
  selectPopups(state).showCuttableMountingRailHint;

/**
 * Select cuttable mounting rail hint alignment
 *
 * @param state
 * @returns {null | string}
 */
export const selectCuttableMountingRailHintAlignment = (
  state: State
): CuttableMountingRailHintAlignment =>
  selectPopups(state).cuttableMountingRailHintAlignment;

/**
 * Select cuttable mounting rail hint check pending
 *
 * @param state
 * @returns {null | string}
 */
export const selectCuttableMountingRailHintCheckPending = (
  state: State
): CuttableMountingRailHintCheckPending =>
  selectPopups(state).cuttableMountingRailHintCheckPending;

/**
 * Select intro popups visible
 *
 * @param state
 * @returns {Requireable<boolean> | boolean}
 */
export const selectIntroPopupsVisible = (state: State): IntroPopupsVisible =>
  selectPopups(state).introPopupsVisible;

/**
 * Select doors popups visible
 *
 * @param state
 * @returns {Requireable<boolean> | boolean}
 */
export const selectShowDoorsPopupHint = (state: State): ShowDoorsHint =>
  selectPopups(state).showDoorsHint;

/**
 * Select has shown filter intro popup
 *
 * @param state
 */
export const selectHasShownFilterIntroPopup = (state: State) =>
  selectPopups(state).hasShownFilterIntroPopup;

/**
 * Select filter intro popup visible
 *
 * @param state
 */
export const selectFilterIntroPopupVisible = (state: State) =>
  selectPopups(state).filterIntroPopupVisible;

/**
 * Select should filter intro popup be visible
 *
 * @param state
 */
export const selectShouldFilterIntroPopupBeVisible = (state: State) =>
  !selectConfDialog(state).open &&
  !selectIsMeasurementsActive(state) &&
  !selectHasShownFilterIntroPopup(state);

/** Select overlay active eg. if any element using an overlay is active
 *
 * @param state
 */
export const selectOverlayActive = (state: State) =>
  selectPopups(state).overlayActive;

/** Select survey visible state
 *
 * @param state
 */
export const selectSurveyVisible = (state: State) =>
  selectPopups(state).surveyVisible;

/** Select survey visible state
 *
 * @param state
 */
export const selectSurveyState = (state: State) =>
  selectPopups(state).surveyState;

/** Select scroll exception eg. if we need to scroll in mobile
 *
 * @param state
 */
export const selectScrollException = (state: State) =>
  selectPopups(state).scrollException;

/* Select has shown intro popups
 *
 * @param state
 */
export const selectHasShownIntroPopups = (state: State) =>
  selectPopups(state).hasShownIntroPopups;

/* Select error handling pending timestamp
 *
 * @param state
 */
export const selectErrorHandlingPendingTimestamp = (
  state: State
): ErrorHandlingPendingTimestamp =>
  selectPopups(state).errorHandlingPendingTimestamp;
